class QFlow {

    private steps : QStep[] = [];
    private current : number = -1;

    /**
     * Instantiate a flow
     */
    constructor() {
        
    }

    /**
     * Add a node to the flow. A node is either automated computation (a JS function) or human-computation
     * (a user interface). The node is wrapped as a QStep, which is returned from this method
     * @param node 
     */
    public add(node: QNode) {
        var step : QStep;
        this.steps.push(step = new QStep(node));
        return step;
    }

    /**
     * Move to the first node, and initialise the state
     */
    public start() : void {

        if(this.steps.length < 1) {
            throw "Cannot start a flow with no steps defined";
        }

        this.current = 0;

        var currentStep = this.steps[this.current];
        currentStep.setInState({});
        this.go(this.steps[0]);
    }

    /**
     * Move to the next step. The current step must be passed in
     */
    public next( state : QStateObject, current : QStep ) : void {

        // Find the current step in the step list
        for(var stepN = 0; this.steps[stepN] !== current; stepN++) {}

        // Then find the next one
        var nextStep : QStep | null = null;
        if(this.steps.length > stepN) {
            nextStep = this.steps[stepN + 1];
        }

        //console.debug("Next step is", nextStep);

        if(!nextStep) {
            throw "There is no next step defined";
        }

        // Cache the incoming state into the step
        //console.debug("Set state", state);
        nextStep.setInState(structuredClone(state));

        // Then run the step
        this.go(nextStep);
    }

    /**
     * Go to a specific step using the given state
     */
    public goto( step : QStep, state : Object ) : void {
        step.setInState(structuredClone(state));
        this.go(step);
    }

    /**
     * Go back to the last 'visible' step
     */
    public back(currentStep) : void {

        for(var i = 0; this.steps[i] !== currentStep; i++) {}
        //console.debug("Current step is", i, currentStep);

        for(var i = i - 1; i >= 0; i--) {
            var step = this.steps[i];

            // We can only go back to steps that are visible (ie not just logical nodes)
            // and which have instate set (i.e. have been used before)
            if(step.getNode().isVisible() && step.hasInState()) {
                this.backTo(step);
                return;
            }
        }

        // Otherwise throw an exception
        throw "Cannot go back in a step list with no prior visible steps";
    }

    /**
     * Go back to the given QStep. The step must be part of the history, otherwise
     * an exception will be thrown
     */
    public backTo( step : QStep ) : void {
        for(var i = 0; i < this.steps.length; i++) {
            if(this.steps[i] === step) {
                if(!step.hasInState()) {
                    throw "Cannot go back to a step that has no previous state";
                }

                this.go(step);

                return;
            }
        }

        // Otherwise throw an exception
        throw "Cannot go to a step that was not found in the step list";
    }

    /**
     * Run the given step. State should be set up on the step before this method is
     * called.
     */
    protected go( step? : QStep | number ) : void {
        // Check the step exists and update the internal pointer
        for(var i = 0; i < this.steps.length; i++) {
            if(this.steps[i] === step) {
                var currentStep = this.steps[i];
                var node = step.getNode();
                var state = structuredClone(currentStep.getInState());
                
                node.go(state, (state : QStateObject, step? : QStep | Number) => {
                    // No argument, go to the next step
                    if(typeof step == 'undefined') {
                        this.next(state, currentStep);
                    } 
                    // Negative numbers go back
                    else if(!isNaN(Number(step)) && Number(step) < 0) {
                        this.back(currentStep);
                    }
                    // Otherwise go to the provided step
                    else if(step instanceof QStep){
                        this.goto(step, state);
                    }
                });

                return;
            }
        }

        // Otherwise throw an exception
        throw "Cannot go to a step that was not found in the step list";
    }


}

// State objects carry state between steps
type QStateObject = Record<string, string | Number >;

// ThenFunctions are used by Nodes to switch to other Nodes
type QThenFunction = (state: QStateObject, step?: QStep | Number) => void;


/**
 * Represents a step in a flow. Steps have a node that defines the step's behaviour and potentially some
 * historic state that represents the state that was passed in last time the step was run; so that we can
 * go back to the step without side-effects caused by later steps
 */
class QStep {
    private node : QNode;
    private inState? : Object;

    constructor(node: QNode) {
        this.node = node;
        this.inState = undefined;
    }

    public hasInState() {
        return typeof this.inState !== 'undefined';
    }

    public getInState() {
        return this.inState ? this.inState : {};
    }

    public setInState(state: Object) {
        this.inState = state;
    }

    public getNode() {
        return this.node;
    }

}

/**
 * A node is a piece of computation that needs to be added to the flow.
 */
abstract class QNode {

    /**
     * Apply the step to the given state. Do something, maybe update the state, and then
     * call then() to move on
     * @param state 
     */
    abstract go(state: Object, then: QThenFunction) : void;

    /**
     * Indicate if the node is visible; i.e whether the flow can stop on it when using back()
     */
    abstract isVisible() : boolean;
}



export { QFlow, QNode, QStep, QThenFunction, QStateObject };